import React, { useEffect, useMemo, useState } from 'react';
import { getCompareAssetCardData } from '../../util/AssetStatusCard.util';
import CompareAssetCardBarChart from '../CompareAssetCardBarChart';
import CompareAssetCardAccordion from '../CompareAssetCardAccordion';
import { useAppStateMonthCompareModalDataStore, useAppStateOwnerFilterStore } from '../../../../common/store/AppStateStore.zustand';

const YearSettlementModal: React.FC = () => {
  const { monthCompareModalData } = useAppStateMonthCompareModalDataStore();
  const { ownerFilter } = useAppStateOwnerFilterStore();
  const [baseYearMonth, setBaseYearMonth] = useState<string>('');
  const [compareYearMonth, setCompareYearMonth] = useState<string>('');

  // 모달 열릴 때 초기값 설정: 기준 = 현재 선택 월, 비교 = 그 다음 월
  useEffect(() => {
    if (!monthCompareModalData?.assetReportList?.length) return;

    const { currentReport, assetReportList } = monthCompareModalData;
    const initialBase = currentReport.yearMonth;
    const initialCompare = assetReportList.find((r) => r.yearMonth !== initialBase)?.yearMonth ?? '';

    setBaseYearMonth(initialBase);
    setCompareYearMonth(initialCompare);
  }, [monthCompareModalData]);

  const handleBaseChange = (yearMonth: string) => {
    setBaseYearMonth(yearMonth);
    // 기준 월과 비교 월이 같아지면 비교 월을 다른 것으로 교체
    if (compareYearMonth === yearMonth) {
      const other = monthCompareModalData?.assetReportList.find((r) => r.yearMonth !== yearMonth)?.yearMonth ?? '';
      setCompareYearMonth(other);
    }
  };

  const handleCompareChange = (yearMonth: string) => {
    setCompareYearMonth(yearMonth);
    // 비교 월과 기준 월이 같아지면 기준 월을 다른 것으로 교체
    if (baseYearMonth === yearMonth) {
      const other = monthCompareModalData?.assetReportList.find((r) => r.yearMonth !== yearMonth)?.yearMonth ?? '';
      setBaseYearMonth(other);
    }
  };

  const compareAssetCardData = useMemo(() => {
    if (!monthCompareModalData?.assetReportList || !baseYearMonth || !compareYearMonth) return undefined;
    let baseReport = monthCompareModalData.assetReportList.find((r) => r.yearMonth === baseYearMonth);
    let prevReport = monthCompareModalData.assetReportList.find((r) => r.yearMonth === compareYearMonth);
    if (!baseReport || !prevReport) return undefined;
    if (ownerFilter) {
      baseReport = { ...baseReport, reportItemList: baseReport.reportItemList.filter((item) => item.owner === ownerFilter) };
      prevReport = { ...prevReport, reportItemList: prevReport.reportItemList.filter((item) => item.owner === ownerFilter) };
    }
    return getCompareAssetCardData(baseReport, prevReport);
  }, [monthCompareModalData?.assetReportList, baseYearMonth, compareYearMonth, ownerFilter]);

  if (!monthCompareModalData?.isOpen) return null;

  const { assetReportList, onClose } = monthCompareModalData;

  const selectClass =
    'appearance-none bg-white border border-gray-200 rounded-lg py-1.5 pl-3 pr-8 text-gray-700 shadow-sm hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 cursor-pointer !rounded-button text-sm transition-all duration-200';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-gray-800">월별 비교</h2>
              {ownerFilter && (
                <span className="bg-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                  {ownerFilter === 'HUSBAND' ? '남편' : '아내'}
                </span>
              )}
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
              <i className="fas fa-times"></i>
            </button>
          </div>
          <div className="flex items-center flex-wrap gap-3 mt-3">
            <span className="text-gray-500 text-sm">기준 월</span>
            <div className="relative">
              <select value={baseYearMonth} onChange={(e) => handleBaseChange(e.target.value)} className={selectClass}>
                {assetReportList.map((r) => (
                  <option key={r.id} value={r.yearMonth}>
                    {r.yearMonth}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <i className="fas fa-chevron-down text-xs"></i>
              </div>
            </div>
            <span className="text-gray-400 text-sm">vs</span>
            <span className="text-gray-500 text-sm">비교 월</span>
            <div className="relative">
              <select value={compareYearMonth} onChange={(e) => handleCompareChange(e.target.value)} className={selectClass}>
                {assetReportList
                  .filter((r) => r.yearMonth !== baseYearMonth)
                  .map((r) => (
                    <option key={r.id} value={r.yearMonth}>
                      {r.yearMonth}
                    </option>
                  ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                <i className="fas fa-chevron-down text-xs"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          {!compareAssetCardData && (
            <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
              <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mb-4">
                <i className="fas fa-wallet text-3xl text-green-300"></i>
              </div>
              <h4 className="text-lg font-medium text-gray-700 mb-2">비교 데이터가 없습니다</h4>
              <p className="text-sm text-gray-500 text-center max-w-sm">비교할 월을 선택해주세요</p>
            </div>
          )}
          {compareAssetCardData && (
            <>
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">자산 변동 요약</h3>
                <CompareAssetCardBarChart {...compareAssetCardData} />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-4">상세 내역</h3>
                <CompareAssetCardAccordion {...compareAssetCardData} />
              </div>
            </>
          )}
        </div>
        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-all duration-200 !rounded-button"
          >
            닫기
          </button>
        </div>
      </div>
    </div>
  );
};

export default YearSettlementModal;
